import {isEmpty} from 'lodash';
import React, {FC, useCallback} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {AsyncActionButton} from '../../../../../../libs/shared/ui/src/components/buttons/loadingButton/asyncActionButton';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ThemedImageNamesEnum} from '../../../../../helpers/imageThemeHelpers';
import {isSandboxInbox, SandboxInboxTypesEnum} from '../../../../../helpers/sandbox/sandboxInboxHelpers';
import {SandboxScenariosEnum} from '../../../../../helpers/sandbox/sandboxScenarioHelpers';
import {generateNewSandboxConversationAction} from '../../../../../store/data/conversations/sandboxConversationsActions';
import {getPinnedSandboxWorkspaceInboxes} from '../../../../../store/data/inboxes/inboxesSelectors';
import {
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {LeaveSandboxCoordinator} from '../../../../onboarding/sandbox/leaveSandboxCoordinator';
import {CtaEmptyState} from './ctaEmptyState';

interface SandboxEmptyStateProps {
  inboxesParameters: InboxesParameters;
}

/*
 * Component.
 */

export const SandboxEmptyState: FC<SandboxEmptyStateProps> = ({inboxesParameters}) => {
  const sandboxInboxes = useAppSelector(getPinnedSandboxWorkspaceInboxes);
  const dispatch = useAppDispatch();

  const primaryButtonRenderTooltip = useCallback(
    () =>
      isEmpty(sandboxInboxes) && (
        <Trans id="sandbox-emptystate-noinboxes">
          You need at least one inbox in your sandbox workspace.
        </Trans>
      ),
    [sandboxInboxes],
  );

  const onGenerateNewEmailAsync = async (parameters: InboxesParameters): Promise<void> => {
    const inboxId = parameters.type === InboxesParametersTypesEnum.INBOX ? parameters.inboxId : undefined;
    const inbox = sandboxInboxes.find((sandboxInbox) => sandboxInbox.id === inboxId);
    const isChatInbox = inbox ? isSandboxInbox(inbox, SandboxInboxTypesEnum.CHAT) : false;
    const isAnalyticsInbox = inbox ? isSandboxInbox(inbox, SandboxInboxTypesEnum.ANALYTICS) : false;

    if (isChatInbox) {
      await dispatch(
        generateNewSandboxConversationAction(inboxId, {
          scenario: SandboxScenariosEnum.SCENARIO4_ACT4,
        }),
      );
      return;
    }

    if (isAnalyticsInbox) {
      await dispatch(
        generateNewSandboxConversationAction(inboxId, {
          scenario: SandboxScenariosEnum.SCENARIO3_ACT3,
        }),
      );
      return;
    }

    const isGeneralSandboxInboxTicketing = inbox?.isTicketingEnabled;

    await dispatch(
      generateNewSandboxConversationAction(inboxId, {
        scenario: isGeneralSandboxInboxTicketing
          ? SandboxScenariosEnum.SCENARIO5_ACT1
          : SandboxScenariosEnum.SCENARIO3_ACT1,
        initialStep: isGeneralSandboxInboxTicketing
          ? 'start-scenario-5-conversation-1'
          : 'start-scenario-3-conversation-1',
      }),
    );
  };

  return (
    <LeaveSandboxCoordinator>
      {({isConfirmLeaveSandboxOpen, onLeaveSandboxClick}) => (
        <CtaEmptyState
          image={ThemedImageNamesEnum.SANDBOX}
          title={<Trans id="sandbox-emptystate-title-explore">Explore Front in an interactive demo</Trans>}
          description={
            <Trans id="sandbox-emptystate-description-explore">
              See how Front&apos;s powerful capabilities help teams
              <br />
              increase efficiency and customer satisfaction.
            </Trans>
          }
        >
          <Button
            isActive={isConfirmLeaveSandboxOpen}
            variant={ButtonVariantsEnum.SECONDARY}
            onClick={() => {
              trackInterfaceEvent(InterfaceEventsEnum.EMPTY_STATE_REMOVE_FROM_SIDEBAR_CLICK);
              onLeaveSandboxClick();
            }}
            size={VisualSizesEnum.MEDIUM}
          >
            <Trans id="sandbox-emptystate-remove">Leave sandbox workspace</Trans>
          </Button>
          <AsyncActionButton
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={async () => {
              trackInterfaceEvent(InterfaceEventsEnum.EMPTY_STATE_GENERATE_NEW_DEMO_EMAIL_CLICK);
              await onGenerateNewEmailAsync(inboxesParameters);
            }}
            size={VisualSizesEnum.MEDIUM}
            renderTooltip={primaryButtonRenderTooltip}
            isDisabled={isEmpty(sandboxInboxes)}
          >
            <Trans id="sandbox-emptystate-newemail">Start new demo</Trans>
          </AsyncActionButton>
        </CtaEmptyState>
      )}
    </LeaveSandboxCoordinator>
  );
};
